/**
 * Converts TipTap HTML output to markdown format with tool annotations
 * Handles conversion of item spans to %{Tool:...} format
 */
export class HtmlToMarkdownConverter {
  constructor() {
    // DOM parser for parsing HTML content
    this.parser = new DOMParser();
  }

  /**
   * Convert HTML content from TipTap editor to markdown with tool annotations
   * @param {string} html - The HTML content from TipTap editor
   * @returns {string} - Markdown content with tool annotations
   */
  convertToMarkdown(html) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    // Parse the HTML
    const doc = this.parser.parseFromString(html, 'text/html');
    // Process the document and convert to markdown
    return this.processNode(doc.body).trim();
  }

  /**
   * Process a DOM node and convert it to markdown
   * @param {Node} node - The DOM node to process
   * @returns {string} - Markdown representation
   */
  processNode(node) {
    if (!node) return '';

    switch (node.nodeType) {
      case Node.TEXT_NODE:
        return HtmlToMarkdownConverter.escapeMarkdownCharacters(
          node.textContent
        );
      case Node.ELEMENT_NODE:
        return this.processElement(node);
      default:
        return '';
    }
  }

  /**
   * Process an HTML element and convert it to markdown
   * @param {Element} element - The HTML element to process
   * @returns {string} - Markdown representation
   */
  processElement(element) {
    const tagName = element.tagName.toLowerCase();
    const content = this.processChildNodes(element);

    switch (tagName) {
      case 'p':
        return content + '\n\n';
      case 'br':
        return '\n';
      case 'strong':
      case 'b':
        return `**${content}**`;
      case 'em':
      case 'i':
        return `*${content}*`;
      case 'code':
        return `\`${content}\``;
      case 'ul':
        return content + '\n';
      case 'ol':
        return content + '\n';
      case 'li':
        return `- ${content}\n`;
      case 'h1':
        return `# ${content}\n\n`;
      case 'h2':
        return `## ${content}\n\n`;
      case 'h3':
        return `### ${content}\n\n`;
      case 'h4':
        return `#### ${content}\n\n`;
      case 'h5':
        return `##### ${content}\n\n`;
      case 'h6':
        return `###### ${content}\n\n`;
      case 'a': {
        const href = element.getAttribute('href');
        return href ? `[${content}](${href})` : content;
      }

      case 'img': {
        const src = element.getAttribute('src');
        const alt = element.getAttribute('alt') || '';
        return src ? `![${alt}](${src})` : '';
      }

      case 'table':
        return content ? `${content}\n\n` : '';

      case 'tr': {
        const cells = Array.from(element.children)
          .filter(n => n.nodeType === Node.ELEMENT_NODE)
          .map(cell => this.processChildNodes(cell).trim());
        if (cells.length === 0) return '';
        return `| ${cells.join(' | ')} |\n`;
      }

      case 'th':
      case 'td':
        return content;

      case 'span':
        return HtmlToMarkdownConverter.processSpanElement(element, content);

      default:
        return content;
    }
  }

  /**
   * Process span elements, especially item references and attribute pills
   * @param {Element} element - The span element
   * @param {string} content - The processed content of the span
   * @returns {string} - Tool annotation or regular content
   */
  static processSpanElement(element, content) {
    const dataType = element.getAttribute('data-type');
    const itemType = element.getAttribute('data-item-type');
    const dataId = element.getAttribute('data-id');

    // Handle item references (tools, attributes, libraries, and visualizations)
    if (dataType === 'item' && dataId) {
      if (itemType === 'tool') {
        return `%{Tool:${dataId}}`;
      }
      if (itemType === 'attribute') {
        // For attributes, use the %{Attribute:...} format
        return `%{Attribute:${dataId}}`;
      }
      if (itemType === 'contact_attribute') {
        // For contact attributes, use the %{ContactAttribute:...} format
        return `%{ContactAttribute:${dataId}}`;
      }
      if (itemType === 'event_attribute' || itemType === 'event_field') {
        return `%{EventAttribute:${dataId}}`;
      }
      if (itemType === 'library') {
        // For libraries, use the %{Library:...} format
        return `%{Library:${dataId}}`;
      }
      if (itemType === 'visualization') {
        // For visualizations, get the visualization ID from the data attribute
        const visualizationId =
          element.getAttribute('data-visualization-id') || dataId;
        return `%{Visualization:${visualizationId}}`;
      }
    }

    // Handle attribute pills
    if (dataType === 'attribute' || dataType === 'contact_attribute') {
      const dataContent = element.getAttribute('data-content');
      const dataTooltip = element.getAttribute('data-tooltip');

      if (dataContent) {
        return dataTooltip
          ? `[${dataContent}](${dataTooltip})`
          : `[${dataContent}]`;
      }
    }

    // Handle other span classes/types if needed
    const className = element.getAttribute('class');
    if (className && className.includes('item-reference')) {
      // Fallback for item references without proper data attributes
      return `%{Tool:${dataId || content}}`;
    }

    // Return the content as-is for regular spans
    return content;
  }

  /**
   * Process all child nodes of an element
   * @param {Element} element - The parent element
   * @returns {string} - Combined markdown content of all children
   */
  processChildNodes(element) {
    const childNodes = Array.from(element.childNodes);
    return childNodes.map(child => this.processNode(child)).join('');
  }

  /**
   * Escape special markdown characters in text content
   * @param {string} text - The text to escape
   * @returns {string} - Escaped text
   */
  static escapeMarkdownCharacters(text) {
    if (!text) return '';

    // Only escape characters that could interfere with our tool annotations
    // Be more conservative to avoid over-escaping
    return text
      .replace(/\\/g, '\\\\')
      .replace(/\[/g, '\\[')
      .replace(/\]/g, '\\]')
      .replace(/`/g, '\\`');
  }

  /**
   * Extract tool references from HTML content
   * @param {string} html - The HTML content from TipTap editor
   * @returns {string[]} - Array of tool/attribute names
   */
  extractToolReferences(html) {
    if (!html || typeof html !== 'string') {
      return [];
    }

    const doc = this.parser.parseFromString(html, 'text/html');
    const toolReferences = new Set();

    // Find all span elements with tool item references (not attributes)
    const itemSpans = doc.querySelectorAll(
      'span[data-type="item"][data-item-type="tool"]'
    );
    itemSpans.forEach(span => {
      const dataId = span.getAttribute('data-id');
      if (dataId) {
        toolReferences.add(dataId);
      }
    });

    // Also check for existing %{Tool:...} patterns in case they're mixed
    const toolMatches = html.match(
      /%\{Tool:([a-zA-Z0-9_-]+)(?::([a-zA-Z0-9_-]+))?\}/g
    );
    if (toolMatches) {
      toolMatches.forEach(match => {
        const toolName = match.match(/%\{Tool:([a-zA-Z0-9_-]+)/)[1];
        if (toolName) {
          toolReferences.add(toolName);
        }
      });
    }

    return Array.from(toolReferences);
  }

  /**
   * Extract attribute references from HTML content
   * @param {string} html - The HTML content from TipTap editor
   * @returns {string[]} - Array of attribute keys/names
   */
  extractAttributeReferences(html) {
    if (!html || typeof html !== 'string') {
      return [];
    }

    const doc = this.parser.parseFromString(html, 'text/html');
    const attributeReferences = new Set();

    // Find all span elements with attribute item references
    const itemSpans = doc.querySelectorAll(
      'span[data-type="item"][data-item-type="attribute"]'
    );
    itemSpans.forEach(span => {
      const dataId = span.getAttribute('data-id');
      if (dataId) {
        attributeReferences.add(dataId);
      }
    });

    // Find all span elements with attribute type (attribute pills)
    const attributeSpans = doc.querySelectorAll('span[data-type="attribute"]');
    attributeSpans.forEach(span => {
      const dataContent = span.getAttribute('data-content');
      if (dataContent) {
        attributeReferences.add(dataContent);
      }
    });

    // Check for existing %{Attribute:...} patterns in markdown format
    const attributeMatches = html.match(/%\{Attribute:([a-zA-Z0-9_-]+)\}/g);
    if (attributeMatches) {
      attributeMatches.forEach(match => {
        const attributeName = match.match(/%\{Attribute:([a-zA-Z0-9_-]+)\}/)[1];
        if (attributeName) {
          attributeReferences.add(attributeName);
        }
      });
    }

    // Check for attribute pill syntax: [content] or [content](tooltip)
    // This is a more cautious approach - only match if it looks like an attribute reference
    const attributePillMatches = html.match(
      /\[([a-zA-Z0-9_\-\s]+)\](?:\([^)]*\))?/g
    );
    if (attributePillMatches) {
      attributePillMatches.forEach(match => {
        const content = match.match(/\[([a-zA-Z0-9_\-\s]+)\]/)[1];
        // Only add if it looks like a reasonable attribute key (no spaces, underscores allowed)
        if (content && /^[a-zA-Z0-9_-]+$/.test(content)) {
          attributeReferences.add(content);
        }
      });
    }

    return Array.from(attributeReferences);
  }

  extractContactAttributeReferences(html) {
    if (!html || typeof html !== 'string') {
      return [];
    }

    const doc = this.parser.parseFromString(html, 'text/html');
    const contactAttributeReferences = new Set();

    // Find all span elements with contact attribute item references
    const itemSpans = doc.querySelectorAll(
      'span[data-type="item"][data-item-type="contact_attribute"]'
    );
    itemSpans.forEach(span => {
      const dataId = span.getAttribute('data-id');
      if (dataId) {
        contactAttributeReferences.add(dataId);
      }
    });

    // Check for existing %{ContactAttribute:...} patterns in markdown format
    const contactAttributeMatches = html.match(
      /%\{ContactAttribute:([a-zA-Z0-9_-]+)\}/g
    );
    if (contactAttributeMatches) {
      contactAttributeMatches.forEach(match => {
        const contactAttributeName = match.match(
          /%\{ContactAttribute:([a-zA-Z0-9_-]+)\}/
        )[1];
        if (contactAttributeName) {
          contactAttributeReferences.add(contactAttributeName);
        }
      });
    }

    return Array.from(contactAttributeReferences);
  }

  extractEventAttributeReferences(html) {
    if (!html || typeof html !== 'string') {
      return [];
    }

    const doc = this.parser.parseFromString(html, 'text/html');
    const eventAttributeReferences = new Set();

    const itemSpans = doc.querySelectorAll(
      'span[data-type="item"][data-item-type="event_attribute"], span[data-type="item"][data-item-type="event_field"]'
    );
    itemSpans.forEach(span => {
      const dataId = span.getAttribute('data-id');
      if (dataId) {
        eventAttributeReferences.add(dataId);
      }
    });

    const eventAttributeMatches = html.match(
      /%\{EventAttribute:([a-zA-Z0-9_-]+)\}/g
    );
    if (eventAttributeMatches) {
      eventAttributeMatches.forEach(match => {
        const key = match.match(/%\{EventAttribute:([a-zA-Z0-9_-]+)\}/)[1];
        if (key) {
          eventAttributeReferences.add(key);
        }
      });
    }

    const eventFieldMatches = html.match(/%\{EventField:([a-zA-Z0-9_-]+)\}/g);
    if (eventFieldMatches) {
      eventFieldMatches.forEach(match => {
        const key = match.match(/%\{EventField:([a-zA-Z0-9_-]+)\}/)[1];
        if (key) {
          eventAttributeReferences.add(key);
        }
      });
    }

    return Array.from(eventAttributeReferences);
  }

  /**
   * Extract library references from HTML content
   * @param {string} html - The HTML content from TipTap editor
   * @returns {string[]} - Array of library namespaces
   */
  extractLibraryReferences(html) {
    if (!html || typeof html !== 'string') {
      return [];
    }

    const doc = this.parser.parseFromString(html, 'text/html');
    const libraryReferences = new Set();

    // Find all span elements with library item references
    const itemSpans = doc.querySelectorAll(
      'span[data-type="item"][data-item-type="library"]'
    );
    itemSpans.forEach(span => {
      const dataId = span.getAttribute('data-id');
      if (dataId) {
        libraryReferences.add(dataId);
      }
    });

    // Also check for existing %{Library:...} patterns in case they're mixed
    const libraryMatches = html.match(/%\{Library:([a-zA-Z0-9_-]+)\}/g);
    if (libraryMatches) {
      libraryMatches.forEach(match => {
        const namespace = match.match(/%\{Library:([a-zA-Z0-9_-]+)\}/)[1];
        if (namespace) {
          libraryReferences.add(namespace);
        }
      });
    }

    return Array.from(libraryReferences);
  }

  /**
   * Extract visualization references from HTML content
   * @param {string} html - The HTML content from TipTap editor
   * @returns {string[]} - Array of visualization IDs
   */
  extractVisualizationReferences(html) {
    if (!html || typeof html !== 'string') {
      return [];
    }

    const doc = this.parser.parseFromString(html, 'text/html');
    const visualizationReferences = new Set();

    // Find all span elements with visualization item references
    const itemSpans = doc.querySelectorAll(
      'span[data-type="item"][data-item-type="visualization"]'
    );
    itemSpans.forEach(span => {
      const visualizationId =
        span.getAttribute('data-visualization-id') ||
        span.getAttribute('data-id');
      if (visualizationId) {
        visualizationReferences.add(visualizationId);
      }
    });

    // Also check for existing %{Visualization:...} patterns in case they're mixed
    const visualizationMatches = html.match(
      /%\{Visualization:([a-zA-Z0-9_-]+)\}/g
    );
    if (visualizationMatches) {
      visualizationMatches.forEach(match => {
        const vizId = match.match(/%\{Visualization:([a-zA-Z0-9_-]+)\}/)[1];
        if (vizId) {
          visualizationReferences.add(vizId);
        }
      });
    }

    return Array.from(visualizationReferences);
  }

  /**
   * Check if the HTML content has meaningful non-whitespace content
   * @param {Element} element - The HTML element to check
   * @returns {boolean} - Whether there's meaningful content
   */
  static hasNonWhitespaceContent(element) {
    // Check if there are any text nodes with actual content
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let node = walker.nextNode();
    while (node) {
      if (node.textContent.trim()) {
        return true;
      }
      node = walker.nextNode();
    }

    // Check for meaningful elements like images, spans with data attributes, etc.
    const meaningfulElements = element.querySelectorAll(
      'img, span[data-type], span[data-content]'
    );
    return meaningfulElements.length > 0;
  }
}

// Export a default instance for convenience
export default new HtmlToMarkdownConverter();
